/**
 * Seite 2 rechts — Portfolio-Bereiche in Excel-Reihenfolge.
 *
 * Bis 10/2026 hat der Mapper die Excel-Bereiche nach Diagrammart auf vier feste Plätze
 * verteilt (1. + 2. Bar-Bereich, 1. Pie-Bereich, 1. Table-Bereich). Gemischte Bereiche
 * wurden umgedeutet, weitere still verworfen — nur das Value-Layout kam richtig an.
 *
 * Jetzt gilt dieselbe Logik wie im Web-Widget (cixon-finance-client
 * `custom-portfolio-widget.tsx`): Bereiche in Excel-Reihenfolge, jedes Element in seiner
 * eigenen Art, Sortierung und gepinnte letzte Zeile wie dort. Das SQUAD-Layout (Navy-Balken,
 * Diagrammkomponenten, Schriftgrößen) bleibt.
 *
 * Gespeichert wird `portfolioSections` im Dokument (Snapshot beim Laden des Uploads).
 * Ältere Dokumente ohne dieses Feld bekommen ihre Bereiche im Mapper aus den bisherigen
 * festen Feldern — sie sehen aus wie vorher.
 *
 * Bewusst anders als im Web: Summe/Kontrollwert-Zeilen entfallen auch in Tabellen (der
 * Server verwirft sie beim Parsen ohnehin), und `_x000D_` (Excel-CR vor dem LF) wird
 * entfernt statt zu einem zweiten Umbruch — sonst stünde im PDF eine Leerzeile.
 */
import {
  getBarColorsFromTheme,
  getPieColorsFromTheme,
  type LabelValueNum,
  type LabelValueStr,
  type SquadPortfolioTheme,
} from './squadCustomPortfolio';

/** Die vier festen Überschriften des bisherigen Layouts (sectionHeaders-Schlüssel). */
export type SquadLegacyPortfolioHeaderKey =
  | 'allokationAktien'
  | 'allokationEvents'
  | 'assetAllokation'
  | 'renditenPortfoliodaten';

export type SquadPortfolioBlock =
  /** Excel-Art „Empty“: hält die Hälfte frei, damit das andere Element seine Spalte behält. */
  | { kind: 'empty' }
  | { kind: 'bar'; items: LabelValueNum[]; color?: string }
  | { kind: 'pie'; items: LabelValueNum[]; colors?: string[] }
  | { kind: 'table'; rows: LabelValueStr[]; header?: LabelValueStr };

export type SquadPortfolioSection = {
  /** Excel-`sectionTitle`. */
  title?: string;
  /** Nur bei umgewandelten Altdokumenten: Überschrift aus `sectionHeaders[headerKey]`. */
  headerKey?: SquadLegacyPortfolioHeaderKey;
  /** 2 = Elemente nebeneinander je halbe Breite, 1 = untereinander in voller Breite. */
  columns: 1 | 2;
  blocks: SquadPortfolioBlock[];
  footnote?: string;
};

type UploadRow = {
  label?: unknown;
  value?: unknown;
  isTemplateLastDataRow?: boolean;
};

type UploadWidget = {
  title?: string;
  art?: string;
  sortierung?: string;
  lastRowForCashOrHeader?: number | string;
  lastRowHasEntry?: boolean;
  templateLastRowLabel?: string;
  pinnedDataRowIndex?: number;
  footnote?: string;
  rows?: UploadRow[];
};

type UploadSection = {
  sectionTitle?: string;
  widgets?: UploadWidget[];
};

type WidgetKind = 'empty' | 'bar' | 'pie' | 'table';

/** Wie `artToType` im Web: Empty zuerst, dann table/tabelle, pie, bar; Unbekanntes = Pie. */
export function squadPortfolioWidgetKind(art?: string): WidgetKind {
  const a = String(art ?? '')
    .trim()
    .toLowerCase();
  if (a === 'empty') return 'empty';
  if (a.includes('table') || a.includes('tabelle')) return 'table';
  if (a.includes('pie')) return 'pie';
  if (a.includes('bar')) return 'bar';
  return 'pie';
}

/** Identisch mit dem Web-Widget (`parseNum`): erstes Komma als Dezimaltrenner, Rest ignorieren. */
export function parseSquadPortfolioNumber(value: unknown): number {
  if (value == null) return 0;
  const s = String(value).replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(s.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

type SortMode = 'default' | 'label-asc' | 'label-desc' | 'value-asc' | 'value-desc';

/** Excel schreibt „lable-asc/desc“ (Tippfehler in der Vorlage) — wie im Web mitgemeint. */
export function normalizeSquadPortfolioSortMode(raw?: string): SortMode {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');
  if (s === 'lable-asc') return 'label-asc';
  if (s === 'lable-desc') return 'label-desc';
  if (s === 'label-asc' || s === 'label-desc' || s === 'value-asc' || s === 'value-desc') return s;
  return 'default';
}

const text = (v: unknown) => String(v ?? '').trim();

/** `_x000D_` (Excel-Export) und CRLF → `\n`. */
const normalizeMultiline = (v: unknown) =>
  String(v ?? '')
    .replace(/_x000D_/gi, '')
    .replace(/\r\n?/g, '\n')
    .trim();

function isSumRow(row: UploadRow): boolean {
  const label = text(row?.label).toLowerCase();
  return label.includes('kontrollwert') || /^summe\s*\(/.test(label);
}

function sortRows<T extends UploadRow>(rows: T[], mode: SortMode): T[] {
  if (mode === 'default') return rows;
  const sorted = [...rows];
  if (mode === 'value-asc')
    sorted.sort((a, b) => parseSquadPortfolioNumber(a.value) - parseSquadPortfolioNumber(b.value));
  if (mode === 'value-desc')
    sorted.sort((a, b) => parseSquadPortfolioNumber(b.value) - parseSquadPortfolioNumber(a.value));
  if (mode === 'label-asc' || mode === 'label-desc') {
    sorted.sort((a, b) => text(a.label).localeCompare(text(b.label), 'de', { sensitivity: 'base' }));
    if (mode === 'label-desc') sorted.reverse();
  }
  return sorted;
}

/** Vorlagen-Kopfzeile „Label | Wert“ (falsch gesetzte Startzeile) — nie Daten, nie Pin (wie im Web). */
function isTemplateHeaderRow(row: UploadRow): boolean {
  return text(row?.label).toLowerCase() === 'label' && /^(value|wert)$/i.test(text(row?.value));
}

/**
 * Gepinnte 16. Datenzeile (Excel „Letzte Zeile für Barmittel bzw. Header“ = 1), aufgelöst
 * auf den ungefilterten Zeilen. Reihenfolge wie im Web: Zeilen-Marker; die Fallbacks
 * `templateLastRowLabel` und `pinnedDataRowIndex` (letzterer nur ohne Marker-Feld) nur,
 * wenn der Server die Zeile als befüllt gemeldet hat (`lastRowHasEntry`).
 */
function pinnedRowIndex(widget: UploadWidget, rows: UploadRow[]): number {
  if (Number(widget.lastRowForCashOrHeader) !== 1 || widget.lastRowHasEntry === false) return -1;
  const acceptable = (i: number) =>
    i >= 0 && i < rows.length && !!rows[i] && !isSumRow(rows[i]) && !isTemplateHeaderRow(rows[i]);
  const byMarker = rows.findIndex((r) => r?.isTemplateLastDataRow === true);
  if (byMarker >= 0) return acceptable(byMarker) ? byMarker : -1;
  if (widget.lastRowHasEntry !== true) return -1;
  const label = text(widget.templateLastRowLabel);
  if (label) {
    const byLabel = rows.findIndex((r) => text(r?.label) === label);
    if (acceptable(byLabel)) return byLabel;
  }
  const hasMarkerField = rows.some((r) => r && Object.prototype.hasOwnProperty.call(r, 'isTemplateLastDataRow'));
  const idx = Number(widget.pinnedDataRowIndex);
  if (!hasMarkerField && Number.isInteger(idx) && acceptable(idx)) return idx;
  return -1;
}

/** Zeilen ohne Summe/Kopfzeile, sortiert; die gepinnte getrennt (Pie/Bar: ans Ende, Tabelle: Kopfzeile). */
function orderedRows(widget: UploadWidget): { body: UploadRow[]; pinned?: UploadRow } {
  const all = widget.rows || [];
  const pin = pinnedRowIndex(widget, all);
  const pinned = pin >= 0 ? all[pin] : undefined;
  const rest = all.filter((r, i) => i !== pin && !!r && !isSumRow(r) && !isTemplateHeaderRow(r));
  return { body: sortRows(rest, normalizeSquadPortfolioSortMode(widget.sortierung)), pinned };
}

const toNumItem = (r: UploadRow): LabelValueNum => ({
  label: normalizeMultiline(r.label),
  value: parseSquadPortfolioNumber(r.value),
});

const toStrItem = (r: UploadRow): LabelValueStr => ({
  label: normalizeMultiline(r.label),
  value: text(r.value),
});

/** Pie/Bar brauchen ein Label; Tabellen Label oder Wert (wie im Web). */
const chartRowRenderable = (r: UploadRow) => text(r?.label) !== '';
const tableRowRenderable = (r: UploadRow) => text(r?.label) !== '' || text(r?.value) !== '';

function buildBlock(
  widget: UploadWidget,
  theme: SquadPortfolioTheme | null,
  farbthemeFund: string
): SquadPortfolioBlock | null {
  const kind = squadPortfolioWidgetKind(widget.art);
  if (kind === 'empty') return { kind: 'empty' };
  const { body, pinned } = orderedRows(widget);
  const widgetTitle = text(widget.title);

  if (kind === 'table') {
    const rows = body.filter(tableRowRenderable).map(toStrItem);
    const header = pinned && tableRowRenderable(pinned) ? toStrItem(pinned) : undefined;
    if (rows.length === 0 && !header) return null;
    return header ? { kind, rows, header } : { kind, rows };
  }

  const items = [...body, ...(pinned ? [pinned] : [])].filter(chartRowRenderable).map(toNumItem);
  if (items.length === 0) return null;
  if (kind === 'pie') {
    const colors = getPieColorsFromTheme(theme, farbthemeFund, widgetTitle);
    return colors?.length ? { kind, items, colors } : { kind, items };
  }
  const palette =
    getBarColorsFromTheme(theme, farbthemeFund, widgetTitle) ||
    getPieColorsFromTheme(theme, farbthemeFund, widgetTitle);
  // Web: alle Balken in der Primärfarbe (colors[0]); die Sekundärfarbe nutzt es nicht.
  const color = palette?.[0] ? String(palette[0]) : undefined;
  return color ? { kind, items, color } : { kind, items };
}

/** Eine Fußnote je Bereich: alle Elemente außer „Empty“, gleiche Texte nur einmal. */
function sectionFootnote(widgets: UploadWidget[]): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const w of widgets) {
    if (squadPortfolioWidgetKind(w?.art) === 'empty') continue;
    const note = normalizeMultiline(w?.footnote);
    if (!note) continue;
    const key = note.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(note);
  }
  return parts.join('\n');
}

/**
 * Upload (`portfolioWidgetsData`) → Bereiche für Seite 2 rechts.
 *
 * Spaltenregel wie im Web: nebeneinander, wenn ≥ 2 Elemente darstellbar sind oder ein
 * „Empty“ neben einem darstellbaren steht (dann bleibt die leere Hälfte frei). Sonst
 * steht das Element in voller Breite. Ein Bereich ohne Daten, aber mit Fußnote bleibt
 * stehen (Überschrift + Fußnote); ganz leere Bereiche entfallen.
 */
export function buildSquadPortfolioSections(
  sections: UploadSection[] | null | undefined,
  theme: SquadPortfolioTheme | null,
  farbthemeFund: string
): SquadPortfolioSection[] {
  if (!Array.isArray(sections)) return [];
  const fund = String(farbthemeFund || '').trim();
  const out: SquadPortfolioSection[] = [];

  for (const section of sections) {
    const widgets = Array.isArray(section?.widgets) ? section.widgets.filter(Boolean) : [];
    const built = widgets.map((w) => buildBlock(w, theme, fund));
    const renderable = built.filter(
      (b): b is Exclude<SquadPortfolioBlock, { kind: 'empty' }> => !!b && b.kind !== 'empty'
    );
    const hasEmptySlot = built.some((b) => b?.kind === 'empty');
    const twoColumns = widgets.length >= 2 && (renderable.length >= 2 || (hasEmptySlot && renderable.length >= 1));

    // Nebeneinander alle Elemente in Excel-Reihenfolge (Platzhalter inklusive), wie im Web.
    const blocks: SquadPortfolioBlock[] = twoColumns ? built.filter((b): b is SquadPortfolioBlock => !!b) : renderable;
    const footnote = sectionFootnote(widgets);
    if (renderable.length === 0 && !footnote) continue;

    const title = normalizeMultiline(section?.sectionTitle);
    out.push({
      ...(title ? { title } : {}),
      columns: twoColumns ? 2 : 1,
      blocks,
      ...(footnote ? { footnote } : {}),
    });
  }
  return out;
}

type LegacyPortfolioFields = {
  allokationAktienLeft?: LabelValueNum[];
  allokationAktienRight?: LabelValueNum[];
  allocationFootnoteAktien?: string;
  allokationEventsLeft?: LabelValueNum[];
  allokationEventsRight?: LabelValueNum[];
  allocationFootnoteEvents?: string;
  assetAllocation?: LabelValueNum[];
  marktkapitalisierung?: LabelValueNum[];
  assetAllocationNote?: string;
  assetAllocationColors?: string[];
  marktkapitalisierungColors?: string[];
  renditenPortfoliodaten?: { left?: LabelValueStr[]; right?: LabelValueStr[]; footnote?: string };
};

const list = <T>(v: T[] | undefined | null): T[] => (Array.isArray(v) ? v : []);

/**
 * Bisherige feste Felder → dieselben vier Bereiche wie vorher (je zwei Hälften,
 * Bereich nur, wenn eine der beiden Listen Einträge hat). Für Dokumente, die vor
 * `portfolioSections` gespeichert wurden, und für die Mapper-Defaults.
 */
export function legacySquadPortfolioSections(fields: LegacyPortfolioFields): SquadPortfolioSection[] {
  const half = (block: SquadPortfolioBlock, hasRows: boolean): SquadPortfolioBlock =>
    hasRows ? block : { kind: 'empty' };
  const out: SquadPortfolioSection[] = [];

  const barSection = (
    headerKey: SquadLegacyPortfolioHeaderKey,
    left: LabelValueNum[],
    right: LabelValueNum[],
    footnote?: string
  ) => {
    if (left.length === 0 && right.length === 0) return;
    out.push({
      headerKey,
      columns: 2,
      blocks: [
        half({ kind: 'bar', items: left }, left.length > 0),
        half({ kind: 'bar', items: right }, right.length > 0),
      ],
      ...(text(footnote) ? { footnote: String(footnote) } : {}),
    });
  };

  barSection(
    'allokationAktien',
    list(fields.allokationAktienLeft),
    list(fields.allokationAktienRight),
    fields.allocationFootnoteAktien
  );
  barSection(
    'allokationEvents',
    list(fields.allokationEventsLeft),
    list(fields.allokationEventsRight),
    fields.allocationFootnoteEvents
  );

  const asset = list(fields.assetAllocation);
  const mcap = list(fields.marktkapitalisierung);
  if (asset.length > 0 || mcap.length > 0) {
    const assetColors = list(fields.assetAllocationColors);
    const mcapColors = list(fields.marktkapitalisierungColors);
    out.push({
      headerKey: 'assetAllokation',
      columns: 2,
      blocks: [
        half({ kind: 'pie', items: asset, ...(assetColors.length ? { colors: assetColors } : {}) }, asset.length > 0),
        half({ kind: 'pie', items: mcap, ...(mcapColors.length ? { colors: mcapColors } : {}) }, mcap.length > 0),
      ],
      ...(text(fields.assetAllocationNote) ? { footnote: String(fields.assetAllocationNote) } : {}),
    });
  }

  const renditen = fields.renditenPortfoliodaten || {};
  const left = list(renditen.left);
  const right = list(renditen.right);
  if (left.length > 0 || right.length > 0) {
    out.push({
      headerKey: 'renditenPortfoliodaten',
      columns: 2,
      blocks: [
        half({ kind: 'table', rows: left }, left.length > 0),
        half({ kind: 'table', rows: right }, right.length > 0),
      ],
      ...(text(renditen.footnote) ? { footnote: String(renditen.footnote) } : {}),
    });
  }
  return out;
}

const KINDS = new Set(['empty', 'bar', 'pie', 'table']);

/** Gespeicherte `portfolioSections` prüfen; `null`, wenn das Dokument keine hat (→ Altfelder). */
export function normalizeSquadPortfolioSections(raw: unknown): SquadPortfolioSection[] | null {
  if (!Array.isArray(raw)) return null;
  return raw
    .filter((s) => s && typeof s === 'object' && Array.isArray(s.blocks))
    .map((s) => ({
      ...(text(s.title) ? { title: String(s.title) } : {}),
      ...(s.headerKey ? { headerKey: s.headerKey as SquadLegacyPortfolioHeaderKey } : {}),
      columns: s.columns === 1 ? 1 : 2,
      // Listen erzwingen: ein gespeichertes `{ kind: 'pie' }` ohne items darf Editor und PDF nicht abstürzen lassen.
      blocks: (s.blocks as any[])
        .filter((b) => b && KINDS.has(b.kind))
        .map((b): SquadPortfolioBlock => {
          if (b.kind === 'empty') return { kind: 'empty' };
          if (b.kind === 'table') return { ...b, rows: Array.isArray(b.rows) ? b.rows : [] };
          return { ...b, items: Array.isArray(b.items) ? b.items : [] };
        }),
      ...(text(s.footnote) ? { footnote: String(s.footnote) } : {}),
    }));
}

/**
 * Die ersten beiden Tortendiagramme (Excel-Reihenfolge) — nur sie haben im Modus
 * „Bild (bisher)“ einen Bild-Slot (`pieChart1/2ImageDataUrl`); weitere werden als Vektor
 * gezeichnet. Rückgabe: Schlüssel „Bereich:Element“ → Slot 1 oder 2.
 */
export function squadRasterPieSlots(sections: SquadPortfolioSection[] | null | undefined): Map<string, 1 | 2> {
  const slots = new Map<string, 1 | 2>();
  (sections || []).forEach((section, si) =>
    section.blocks.forEach((block, bi) => {
      if (block.kind === 'pie' && block.items.length > 0 && slots.size < 2) {
        slots.set(`${si}:${bi}`, (slots.size + 1) as 1 | 2);
      }
    })
  );
  return slots;
}

/** Daten der beiden Bild-Slots (für die Aufnahme in der Vorschau). */
export function squadRasterPieData(
  sections: SquadPortfolioSection[] | null | undefined
): Array<{ items: LabelValueNum[]; colors: string[] }> {
  const result: Array<{ items: LabelValueNum[]; colors: string[] }> = [];
  const slots = squadRasterPieSlots(sections);
  (sections || []).forEach((section, si) =>
    section.blocks.forEach((block, bi) => {
      const slot = slots.get(`${si}:${bi}`);
      if (slot && block.kind === 'pie') result[slot - 1] = { items: block.items, colors: block.colors || [] };
    })
  );
  return result;
}
